import React, { useState } from "react";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import { Form } from "react-bootstrap";
import { useSelector } from "react-redux";
import IconButton from "@mui/material/IconButton";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";

// ─── Cyber Toggle (used for global select all only) ───
const CyberToggle = ({ checked, onChange, label }) => {
  const id = `cyber-toggle-${label ? label.replace(/\s+/g, "-") : "global"}-${Math.random().toString(36).slice(2, 9)}`;

  return (
    <div className="cyber-toggle-wrapper">
      <input
        type="checkbox"
        className="cyber-toggle-checkbox"
        id={id}
        checked={checked}
        onChange={onChange}
      />
      <label htmlFor={id} className="cyber-toggle">
        <div className="cyber-toggle-track">
          <div className="cyber-toggle-track-glow" />
          <div className="cyber-toggle-track-dots">
            <span className="cyber-toggle-track-dot" />
            <span className="cyber-toggle-track-dot" />
            <span className="cyber-toggle-track-dot" />
          </div>
        </div>
        <div className="cyber-toggle-thumb">
          <div className="cyber-toggle-thumb-shadow" />
          <div className="cyber-toggle-thumb-highlight" />
          <div className="cyber-toggle-thumb-icon">
            <svg viewBox="0 0 24 24">
              <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" />
            </svg>
          </div>
        </div>
        <div className="cyber-toggle-particles">
          <span className="cyber-toggle-particle" />
          <span className="cyber-toggle-particle" />
          <span className="cyber-toggle-particle" />
          <span className="cyber-toggle-particle" />
        </div>
      </label>
    </div>
  );
};

// ─── Mini Cyber Toggle for individual permissions ───
const MiniCyberToggle = ({ checked, onChange }) => {
  const id = `mini-toggle-${Math.random().toString(36).slice(2, 9)}`;

  return (
    <div className="mini-cyber-toggle-wrapper">
      <input
        type="checkbox"
        className="mini-cyber-toggle-checkbox"
        id={id}
        checked={checked}
        onChange={onChange}
      />
      <label htmlFor={id} className="mini-cyber-toggle">
        <div className="mini-cyber-toggle-track">
          <div className="mini-cyber-toggle-glow" />
        </div>
        <div className="mini-cyber-toggle-thumb">
          <div className="mini-cyber-toggle-thumb-icon">
            <svg viewBox="0 0 24 24">
              <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" />
            </svg>
          </div>
        </div>
      </label>
    </div>
  );
};

const RolePermission = ({
  user,
  designationId,
  saveMasterRolePermissionsPath,
  dispatch,
  commonSaveData,
  initialData,
  handleModalClose1,
  staffId,
  isPopup,
  designationData = null,
}) => {
  const [permissions, setPermissions] = useState(initialData || []);
  const [globalToggle, setGlobalToggle] = useState(false);
  const [validated, setValidated] = useState(false);

  const [formData, setFormData] = useState({
    userId: user?.user_id || "",
    designationId: designationData?.designationId || "",
    designationName: designationData?.designationName || "",
  });

  // Read global theme from Redux (same as your chart component)
  const themeStore = useSelector((state) => state.theme);
  const isDark = themeStore?.theme === "dark" || !themeStore?.theme; // fallback to dark

  // Global toggle – enable/disable ALL permissions
  const handleGlobalToggleChange = (e) => {
    const checked = e.target.checked;
    const newValue = checked ? "1" : "0";
    setPermissions(
      permissions.map((row) => {
        const updated = { ...row };
        ["view", "create", "edit", "delete", "export", "approve", "reject"].forEach((key) => {
          if (updated[key] != null) updated[key] = newValue;
        });
        return updated;
      })
    );
    setGlobalToggle(checked);
  };

  const handleDesignationChange = (e) => {
    setFormData((prev) => ({ ...prev, designationName: e.target.value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!e.currentTarget.checkValidity()) {
      setValidated(true);
      return;
    }
    dispatch(
      commonSaveData({
        path: saveMasterRolePermissionsPath,
        data: {
          staffId,
          designationName: formData.designationName,
          designationId: formData.designationId,
          userId: user.user_id,
          permissions,
        },
      })
    );
  };

  const triggerHiddenSubmit = () => {
    if (isPopup) document.querySelector("#hidden-submit-btn")?.click();
  };

  const isModuleEnabled = (mainModuleId) => {
    const children = permissions.filter((r) => r.parentId === mainModuleId);
    if (children.length === 0) {
      const parent = permissions.find((r) => r.moduleId === mainModuleId);
      return ["view", "create", "edit", "delete", "export", "approve", "reject"].some(
        (k) => parent?.[k] === "1"
      );
    }
    return children.some((child) =>
      ["view", "create", "edit", "delete", "export", "approve", "reject"].some((k) => child[k] === "1")
    );
  };

  const toggleModuleAll = (mainModuleId, checked) => {
    const value = checked ? "1" : "0";
    setPermissions((prev) =>
      prev.map((row) => {
        if (row.parentId === mainModuleId || row.moduleId === mainModuleId) {
          const updated = { ...row };
          ["view", "create", "edit", "delete", "export", "approve", "reject"].forEach((key) => {
            if (updated[key] != null) updated[key] = value;
          });
          return updated;
        }
        return row;
      })
    );
  };

  const toggleSinglePermission = (moduleId, field, checked) => {
    setPermissions((prev) =>
      prev.map((row) =>
        row.moduleId === moduleId ? { ...row, [field]: checked ? "1" : "0" } : row
      )
    );
  };

  const getChildren = (parentId) => permissions.filter((r) => r.parentId === parentId);

  const mainModules = permissions.filter((r) => r.isMainModule === "1");

  // ─── STYLES ─── (updated cards bg + header)
  const css = `
    .modules-grid {
      display: grid;
      grid-template-columns: repeat(2, 1fr);
      gap: 14px;
    }

    @media (max-width: 900px) {
      .modules-grid {
        grid-template-columns: 1fr;
      }
    }

    .module-card {
      background: ${isDark ? "linear-gradient(135deg, #141414, #0a0a0a)" : "#f3f4f6"};
      border: 1px solid ${isDark ? "#2a2a2a" : "#d1d5db"};
      border-radius: 12px;
      overflow: hidden;
      display: flex;
      flex-direction: column;
      min-height: 0;
      font-size: 15px;
      transition: all 0.3s ease;
    }

    .module-card:hover {
      transform: translateY(-4px);
      box-shadow: ${isDark ? "0 12px 30px rgba(124,58,237,0.25)" : "0 12px 30px rgba(0,0,0,0.12)"};
    }

    .module-card.enabled {
      border-color: ${isDark ? "#7c3aed" : "#8b5cf6"};
      box-shadow: 0 0 16px ${isDark ? "rgba(124,58,237,0.35)" : "rgba(139,92,246,0.3)"};
    }

    .module-header {
      padding: 10px 14px;
      background: ${isDark ? "rgb(30, 24, 48)" : "rgba(139,92,246,0.04)"};
      display: flex;
      justify-content: space-between;
      align-items: center;
      border-bottom: 1px solid ${isDark ? "#361d455e" : "#e5e5e5"};
      flex-shrink: 0;
    }

    .module-title {
      font-size: 1.3rem;
      font-weight: 700;
      color: ${isDark ? "#f0f0f0" : "#232323"};
      display: flex;
      align-items: center;
      gap: 10px;
    }

    .module-icon {
      width: 32px;
      height: 32px;
      background: ${isDark ? "linear-gradient(135deg, #6d28d9, #a855f7)" : "linear-gradient(135deg, #8b5cf6, #a78bfa)"};
      border-radius: 6px;
      display: flex;
      align-items: center;
      justify-content: center;
      color: white;
      font-weight: bold;
      font-size: 1.15rem;
    }

    .table-wrapper {
      flex: 1 1 auto;
      overflow: hidden;
    }

    .permissions-table {
      width: 100%;
      border-collapse: collapse;
      table-layout: auto;
      margin: 0;
    }

    .permissions-table tbody tr:first-child td {
      border-top: none;
    }

    .permissions-table th,
    .permissions-table td {
      padding: 8px;
      font-size: 14px;
      text-align: center;
      border-top: 1px solid ${isDark ? "#1e1e1e" : "#f0f0f0"};
      white-space: nowrap;
    }

    .permissions-table th {
      background: ${isDark ? "rgba(124,58,237,0.07)" : "rgba(139,92,246,0.05)"};
      color: ${isDark ? "#ffffff" : "#000000"};
      font-weight: 700;
      font-size: 12.5px;
      text-transform: uppercase;
    }

    .permissions-table td:first-child {
      text-align: left;
      padding-left: 12px;
      color: ${isDark ? "#ffffff" : "#232323"};
      font-weight: 600;
      white-space: normal;
      word-break: break-word;
      max-width: 160px;
      font-size: 15px;
    }

    .permissions-table tbody tr:last-child td {
      padding-bottom: 10px;
    }

    /* Mini Cyber Toggle – Light Purple for Light Theme */
    .mini-cyber-toggle-wrapper {
      display: flex;
      justify-content: center;
      align-items: center;
      min-width: 44px;
      padding: 0 2px;
    }

    .mini-cyber-toggle {
      position: relative;
      display: inline-block;
      width: 42px;
      height: 22px;
      cursor: pointer;
    }

    .mini-cyber-toggle-checkbox { 
      position: absolute; 
      opacity: 0; 
      width: 0; 
      height: 0; 
    }

    .mini-cyber-toggle-track {
      position: absolute;
      inset: 0;
      background: ${isDark ? "#111" : "#e0e0e0"};
      border-radius: 11px;
      box-shadow: inset 0 0 3px ${isDark ? "#000" : "#ccc"};
    }

    .mini-cyber-toggle-glow {
      position: absolute;
      inset: 0;
      background: ${isDark ? "linear-gradient(90deg, #423251, #7c3aed)" : "linear-gradient(90deg, #a78bfa, #8b5cf6)"};
      opacity: 0;
      border-radius: 11px;
      transition: opacity 0.25s;
    }

    .mini-cyber-toggle-thumb {
      position: absolute;
      top: 3px;
      left: 3px;
      width: 16px;
      height: 16px;
      background: ${isDark ? "#222" : "#fff"};
      border-radius: 50%;
      transition: left 0.25s ease;
      display: flex;
      align-items: center;
      justify-content: center;
    }

    .mini-cyber-toggle-thumb-icon {
      display: flex;
      align-items: center;
      justify-content: center;
    }

    .mini-cyber-toggle-thumb-icon svg {
      width: 10px;
      height: 10px;
      fill: ${isDark ? "#a6a5a5" : "#8b5cf6"};
      transition: fill 0.25s ease;
    }

    .mini-cyber-toggle-checkbox:checked + .mini-cyber-toggle .mini-cyber-toggle-glow {
      opacity: 0.7;
    }

    .mini-cyber-toggle-checkbox:checked + .mini-cyber-toggle .mini-cyber-toggle-thumb {
      left: 23px;
    }

    .mini-cyber-toggle-checkbox:checked + .mini-cyber-toggle .mini-cyber-toggle-thumb-icon svg {
      fill: ${isDark ? "#c084fc" : "#8b5cf6"};
    }

    /* Cyber Toggle – Light Purple for Light Theme */
    .cyber-toggle-wrapper {
      display: inline-flex;
      flex-direction: column;
      align-items: center;
      position: relative;
      padding: 0;
    }

    .cyber-toggle-checkbox {
      position: absolute;
      opacity: 0;
      width: 0;
      height: 0;
    }

    .cyber-toggle {
      position: relative;
      display: inline-block;
      width: 64px;
      height: 32px;
      cursor: pointer;
    }

    .cyber-toggle-track {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background: ${isDark ? "#111" : "#e0e0e0"};
      border-radius: 16px;
      overflow: hidden;
      box-shadow:
        0 4px 8px ${isDark ? "rgba(0, 0, 0, 0.5)" : "rgba(139, 92, 246, 0.1)"},
        inset 0 0 4px ${isDark ? "rgba(0, 0, 0, 0.8)" : "rgba(139, 92, 246, 0.1)"};
      transition: all 0.4s cubic-bezier(0.3, 1.5, 0.7, 1);
    }

    .cyber-toggle-track::before {
      content: "";
      position: absolute;
      inset: 2px;
      border-radius: 14px;
      background: ${isDark ? "#222" : "#f5f5f5"};
      box-shadow: inset 0 0 5px ${isDark ? "rgba(0, 0, 0, 0.5)" : "rgba(139, 92, 246, 0.05)"};
      z-index: 0;
      transition: all 0.4s ease;
    }

    .cyber-toggle-track-glow {
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: ${isDark ? "linear-gradient(90deg, #a12eff, #4a00e0)" : "linear-gradient(90deg, #a78bfa, #8b5cf6)"};
      opacity: 0;
      border-radius: 16px;
      z-index: 1;
      transition: all 0.4s ease;
    }

    .cyber-toggle-thumb {
      position: absolute;
      top: 4px;
      left: 4px;
      width: 24px;
      height: 24px;
      background: ${isDark ? "#151515" : "#fff"};
      border-radius: 50%;
      z-index: 2;
      transition: all 0.4s cubic-bezier(0.3, 1.5, 0.7, 1);
      box-shadow: 0 2px 5px ${isDark ? "rgba(0, 0, 0, 0.4)" : "rgba(139, 92, 246, 0.15)"};
    }

    .cyber-toggle-thumb-shadow {
      position: absolute;
      inset: 0;
      border-radius: 50%;
      background: radial-gradient(circle at 30% 30%, rgba(255, 255, 255, 0.1), transparent 70%);
      z-index: 1;
    }

    .cyber-toggle-thumb-highlight {
      position: absolute;
      inset: 0;
      border-radius: 50%;
      background: radial-gradient(circle at 70% 70%, rgba(0, 0, 0, 0.2), transparent 70%);
      z-index: 1;
    }

    .cyber-toggle-thumb-icon {
      position: absolute;
      inset: 0;
      display: flex;
      justify-content: center;
      align-items: center;
      z-index: 2;
      opacity: 0.7;
      transition: opacity 0.4s ease, transform 0.4s ease;
    }

    .cyber-toggle-thumb-icon svg {
      width: 14px;
      height: 14px;
      fill: ${isDark ? "#555" : "#8b5cf6"};
      transition: fill 0.4s ease, transform 0.4s ease;
    }

    .cyber-toggle-track-dots {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      display: flex;
      justify-content: flex-end;
      align-items: center;
      padding-right: 10px;
      z-index: 1;
    }

    .cyber-toggle-track-dot {
      width: 3px;
      height: 3px;
      border-radius: 50%;
      background: ${isDark ? "#444" : "#a78bfa"};
      margin-left: 3px;
      opacity: 0.5;
      transition: all 0.4s ease;
    }

    .cyber-toggle-particles {
      position: absolute;
      inset: 0;
      z-index: 0;
      overflow: hidden;
      pointer-events: none;
    }

    .cyber-toggle-particle {
      position: absolute;
      width: 3px;
      height: 3px;
      background: ${isDark ? "#4f156e" : "#8b5cf6"};
      border-radius: 50%;
      opacity: 0;
      filter: blur(1px);
      transition: all 0.3s ease;
      box-shadow: 0 0 4px ${isDark ? "rgba(3, 233, 244, 0.8)" : "rgba(139, 92, 246, 0.8)"};
    }

    .cyber-toggle-particle:nth-child(1) { top: 15%; right: 20%; }
    .cyber-toggle-particle:nth-child(2) { top: 45%; right: 30%; }
    .cyber-toggle-particle:nth-child(3) { top: 25%; right: 40%; }
    .cyber-toggle-particle:nth-child(4) { top: 60%; right: 15%; }

    .cyber-toggle-checkbox:checked + .cyber-toggle .cyber-toggle-track-glow {
      opacity: 0.5;
    }

    .cyber-toggle-checkbox:checked + .cyber-toggle .cyber-toggle-thumb {
      left: calc(100% - 28px);
      background: ${isDark ? "#222" : "#fff"};
    }

    .cyber-toggle-checkbox:checked + .cyber-toggle .cyber-toggle-thumb-icon {
      transform: rotate(360deg);
    }

    .cyber-toggle-checkbox:checked + .cyber-toggle .cyber-toggle-thumb-icon svg {
      fill: ${isDark ? "#03e9f4" : "#8b5cf6"};
    }

    .cyber-toggle-checkbox:checked + .cyber-toggle .cyber-toggle-track-dot {
      background: ${isDark ? "#03e9f4" : "#8b5cf6"};
      box-shadow: 0 0 4px ${isDark ? "#03e9f4" : "rgba(139, 92, 246, 0.8)"};
      opacity: 1;
    }

    .cyber-toggle-checkbox:checked + .cyber-toggle .cyber-toggle-particle {
      opacity: 1;
      animation: cyber-toggle-float 3s infinite alternate;
    }

    .cyber-toggle-checkbox:checked + .cyber-toggle .cyber-toggle-particle:nth-child(1) { animation-delay: 0s; }
    .cyber-toggle-checkbox:checked + .cyber-toggle .cyber-toggle-particle:nth-child(2) { animation-delay: 0.5s; }
    .cyber-toggle-checkbox:checked + .cyber-toggle .cyber-toggle-particle:nth-child(3) { animation-delay: 1s; }
    .cyber-toggle-checkbox:checked + .cyber-toggle .cyber-toggle-particle:nth-child(4) { animation-delay: 1.5s; }

    .cyber-toggle:hover .cyber-toggle-track::before {
      background: ${isDark ? "#272727" : "#ececec"};
    }

    .cyber-toggle:hover .cyber-toggle-thumb {
      box-shadow: 0 2px 8px ${isDark ? "rgba(0, 0, 0, 0.6)" : "rgba(139, 92, 246, 0.25)"};
    }

    .cyber-toggle-checkbox:checked + .cyber-toggle:hover .cyber-toggle-track-glow {
      opacity: 0.7;
    }

    .cyber-toggle-checkbox:focus + .cyber-toggle {
      outline: none;
    }

    .cyber-toggle-checkbox:focus + .cyber-toggle::after {
      content: "";
      position: absolute;
      inset: -4px;
      border-radius: 20px;
      border: 2px solid ${isDark ? "rgb(92, 58, 100)" : "rgba(139, 92, 246, 0.5)"};
      opacity: 0.5;
    }

    @keyframes cyber-toggle-float {
      0% { transform: translateY(0); }
      50% { transform: translateY(-4px); }
      100% { transform: translateY(0); }
    }
  `;

  return (
    <>
      <style>{css}</style>

      {isPopup ? (
        <>
          <DialogTitle style={{ padding: 0, background: "transparent" }}>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                padding: "16px 24px",
                background: isDark ? "#1E1830" : "#ffffff",
                borderBottom: `1px solid ${isDark ? "#40254a94" : "#e5e7eb"}`,
                color: isDark ? "#f4e0ff" : "#111827",
                borderTopLeftRadius: "12px",
                borderTopRightRadius: "12px",
              }}
            >
              {/* Back button + Title */}
              <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                <IconButton
                  onClick={handleModalClose1}
                  size="small"
                  sx={{
                    background: "linear-gradient(135deg, #8B5CF6, #D8B4FE)", // purple gradient
                    color: "#fff", // white icon
                    borderRadius: "50%",
                    width: 45,
                    height: 45,
                    boxShadow: "0 4px 8px rgba(0,0,0,0.1)", // subtle shadow
                    transition: "all 0.3s ease",
                    "&:hover": {
                      transform: "scale(1.08)",
                      boxShadow: "0 6px 12px rgba(0,0,0,0.15)"
                    },
                  }}
                >
                  <ArrowBackIcon sx={{ fontSize: 24 }} />
                </IconButton>
                <span style={{ fontSize: "1.45rem", fontWeight: 700 }}>
                  Role Permissions
                </span>
              </div>

              <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                <span style={{ fontSize: "1.05rem", color: isDark ? "#f9f9f9" : "#495057", fontWeight: 500 }}>
                  Select All
                </span>
                <CyberToggle
                  checked={globalToggle}
                  onChange={handleGlobalToggleChange}
                  label="global-select-all"
                />
                <button
                  className="universal-btn-secondary"
                  onClick={handleModalClose1}
                  style={{
                    background: isDark ? "#382b45" : "#e5e7eb",
                    color: isDark ? "#ffffff" : "#374151",
                    border: `1px solid ${isDark ? "#36164981" : "#d1d5db"}`,
                    padding: "8px 20px",
                    borderRadius: "6px",
                    cursor: "pointer",
                    fontSize: "1rem",
                    fontWeight: 500,
                    transition: "all 0.3s ease"
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.background = isDark ? "#3a3a5c" : "#d1d5db";
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.background = isDark ? "#2a2a3e" : "#e5e7eb";
                  }}
                >
                  Cancel
                </button>
                <button className="universal-btn" onClick={triggerHiddenSubmit}>
                  Save
                </button>
              </div>
            </div>
          </DialogTitle>

          <DialogContent style={{
            padding: "20px",
            background: isDark ? "#0f0f1a" : "#ffffff"
          }}>
            <div style={{ maxHeight: "78vh", overflowY: "auto" }}>
              <Form noValidate validated={validated} onSubmit={handleSubmit}>
                <Form.Group className="mb-4">
                  <Form.Label style={{ fontSize: "1.1rem", fontWeight: 500, color: isDark ? "#ffffff" : "#212529" }}>
                    Designation Name
                  </Form.Label>
                  <Form.Control
                    required
                    value={formData.designationName}
                    onChange={handleDesignationChange}
                    placeholder="Enter designation name"
                    style={{
                      fontSize: "1.05rem",
                      width: "60%",
                      maxWidth: "400px",
                      background: isDark ? "#2e1e38b6" : "#ffffff",
                      border: `1px solid ${isDark ? "#3a3a5c" : "#ccc"}`,
                      color: isDark ? "#ffffff" : "#212529",
                      borderRadius: "8px",
                      padding: "12px",
                    }}
                  />
                  <Form.Control.Feedback type="invalid" style={{ color: "#ff6b6b" }}>
                    Please enter designation name
                  </Form.Control.Feedback>
                </Form.Group>

                <button type="submit" id="hidden-submit-btn" style={{ display: "none" }}>
                  hidden
                </button>
              </Form>

              <div className="modules-grid">
                {mainModules.map((mod) => {
                  const children = getChildren(mod.moduleId);
                  if (children.length === 0) return null;

                  const enabled = isModuleEnabled(mod.moduleId);

                  const visibleKeys = ["view", "create", "edit", "delete", "export", "approve", "reject"].filter(
                    (key) => children.some((c) => c[key] === "1" || c[key] === "0")
                  );

                  if (visibleKeys.length === 0) return null;

                  return (
                    <div key={mod.moduleId} className={`module-card ${enabled ? "enabled" : ""}`}>
                      <div className="module-header">
                        <div className="module-title">
                          <div className="module-icon">{mod.moduleName.charAt(0)}</div>
                          {mod.moduleName}
                        </div>
                        <CyberToggle
                          checked={enabled}
                          onChange={(e) => toggleModuleAll(mod.moduleId, e.target.checked)}
                          label={mod.moduleName}
                        />
                      </div>

                      <div className="table-wrapper">
                        <table className="permissions-table">
                          <thead>
                            <tr>
                              <th>Submodule</th>
                              {visibleKeys.map((key) => (
                                <th key={key}>{key.charAt(0).toUpperCase() + key.slice(1)}</th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {children.map((child) => (
                              <tr key={child.moduleId}>
                                <td>{child.moduleName}</td>
                                {visibleKeys.map((key) => (
                                  <td key={key}>
                                    <MiniCyberToggle
                                      checked={child[key] === "1"}
                                      onChange={(e) =>
                                        toggleSinglePermission(child.moduleId, key, e.target.checked)
                                      }
                                    />
                                  </td>
                                ))}
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </DialogContent>
        </>
      ) : (
        <div style={{ padding: "12px 16px", background: isDark ? "linear-gradient(135deg, #0f0f1a, #141422)" : "#ffffff" }}>
          <div className="modules-grid">
            {mainModules.map((mod) => {
              const children = getChildren(mod.moduleId);
              if (children.length === 0) return null;

              const enabled = isModuleEnabled(mod.moduleId);

              const visibleKeys = ["view", "create", "edit", "delete", "export", "approve", "reject"].filter(
                (key) => children.some((c) => c[key] === "1" || c[key] === "0")
              );

              if (visibleKeys.length === 0) return null;

              return (
                <div key={mod.moduleId} className={`module-card ${enabled ? "enabled" : ""}`}>
                  <div className="module-header">
                    <div className="module-title">
                      <div className="module-icon">{mod.moduleName.charAt(0)}</div>
                      {mod.moduleName}
                    </div>
                    <CyberToggle
                      checked={enabled}
                      onChange={(e) => toggleModuleAll(mod.moduleId, e.target.checked)}
                      label={mod.moduleName}
                    />
                  </div>

                  <div className="table-wrapper">
                    <table className="permissions-table">
                      <thead>
                        <tr>
                          <th>Submodule</th>
                          {visibleKeys.map((key) => (
                            <th key={key}>{key.charAt(0).toUpperCase() + key.slice(1)}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {children.map((child) => (
                          <tr key={child.moduleId}>
                            <td>{child.moduleName}</td>
                            {visibleKeys.map((key) => (
                              <td key={key}>
                                <MiniCyberToggle
                                  checked={child[key] === "1"}
                                  onChange={(e) =>
                                    toggleSinglePermission(child.moduleId, key, e.target.checked)
                                  }
                                />
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </>
  );
};

export default RolePermission;